import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Material, CreateMaterial, UpdateMaterial } from '@estimator/shared';

export function useMaterials(projectId: string) {
  return useQuery({
    queryKey: ['materials', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ materials: Material[] } | Material[]>(
        `/projects/${projectId}/materials`,
      );
      if (Array.isArray(data)) return data;
      return (data as { materials: Material[] }).materials ?? [];
    },
    enabled: !!projectId,
  });
}

export function useCreateMaterial(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateMaterial) => {
      const { data } = await apiClient.post<Material>(`/projects/${projectId}/materials`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials', projectId] }),
  });
}

export function useUpdateMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId, ...body }: UpdateMaterial & { id: string; projectId: string }) => {
      const { data } = await apiClient.patch<Material>(`/projects/${projectId}/materials/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['materials', vars.projectId] });
    },
  });
}

export function useDeleteMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      await apiClient.delete(`/projects/${projectId}/materials/${id}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['materials', vars.projectId] });
    },
  });
}
